<!-- components/ImageGallery.vue -->
<template>
  <div>
    <div class="grid grid-cols-12 gap-3 auto-rows-[130px] sm:gap-4 sm:auto-rows-[180px] md:auto-rows-[220px] lg:auto-rows-[240px]">
      <div v-for="(image, index) in imagesGrid" :key="index" :class="image.cols" class="h-full">
        <div
          class="group relative h-full overflow-hidden rounded shadow-sm dark:shadow-gray-800 cursor-pointer"
          @click="openLightbox(index)"
        >
          <NuxtImg
            :src="image.src"
            :alt="image.alt"
            :title="image.title"
            :width="image.width"
            :height="image.height"
            format="webp"
            loading="lazy"
            sizes="xs:100vw sm:50vw md:33vw lg:33vw"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div
            class="absolute inset-0 group-hover:bg-slate-900/50 duration-500 ease-in-out"
          ></div>
          <div
            class="absolute top-1/2 -translate-y-1/2 start-0 end-0 text-center opacity-0 group-hover:opacity-100 duration-500"
          >
            <div
              class="inline-flex justify-center items-center size-10 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-red-500 transition-colors duration-300"
            >
              <Icon name="mdi:camera" class="size-5" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <UiLightbox
      v-model="lightboxOpen"
      :images="lightboxImages"
      :start-index="currentImageIndex"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';

type GridImage = { url?: string; src?: string; alt?: string };

const props = defineProps({
  images: {
    type: Array as PropType<GridImage[]>,
    default: () => [],
  },
  listingTitle: {
    type: String,
    default: '',
  },
});

const buildCaption = (alt?: string) => {
  const title = props.listingTitle?.trim();
  const description = alt?.trim();
  if (title && description) return `${title} — ${description}`;
  return title || description || 'Apartment photo';
};

const lightboxOpen = ref(false);
const currentImageIndex = ref(0);

const openLightbox = (index: number) => {
  currentImageIndex.value = index;
  lightboxOpen.value = true;
};

// Compute images for the lightbox (standardized format)
const lightboxImages = computed(() =>
  props.images.map((image) => ({
    src: image.url || image.src || '', // Handle potentially different data structures
    alt: buildCaption(image.alt),
  }))
);

function calculateColSpans(count: number) {
  const resultMd: number[] = [];
  const resultSm: number[] = [];

  // MD layout base: [8, 4, 4, 8]
  const mdBase = [8, 4, 4, 8];
  for (let i = 0; i < Math.min(count, 4); i++) {
    resultMd.push(mdBase[i]!); // Assert non-null as we verify bounds
  }
  for (let i = 4; i < count; i++) {
    resultMd.push(4);
  }

  // SM layout base: [7, 5, 5, 7]
  const smBase = [7, 5, 5, 7];
  for (let i = 0; i < Math.min(count, 4); i++) {
    resultSm.push(smBase[i]!); // Assert non-null as we verify bounds
  }
  for (let i = 4; i < count; i++) {
    resultSm.push(4);
  }

  // Adjust md: fill rows to 12
  let i = 0;
  while (i < resultMd.length) {
    let rowTotal = 0;
    const rowStart = i;
    while (i < resultMd.length && resultMd[i] !== undefined && rowTotal + resultMd[i]! <= 12) {
      rowTotal += resultMd[i]!;
      i++;
    }
    if (rowTotal < 12 && i > rowStart) {
      resultMd[i - 1] += 12 - rowTotal;
    }
  }

  // Adjust sm: fill rows to 12
  i = 0;
  while (i < resultSm.length) {
    let rowTotal = 0;
    const rowStart = i;
    while (i < resultSm.length && resultSm[i] !== undefined && rowTotal + resultSm[i]! <= 12) {
      rowTotal += resultSm[i]!;
      i++;
    }
    if (rowTotal < 12 && i > rowStart) {
      resultSm[i - 1] += 12 - rowTotal;
    }
  }

  // Return combined class strings
  const styleClass = resultMd.map(
    (mdSpan, idx) => `md:col-span-${mdSpan} col-span-${resultSm[idx]}`
  );

  return styleClass;
}

const imagesGrid = computed(() => {
  const spans = calculateColSpans(props.images.length);
  return props.images.map((image, index) => {
    const caption = buildCaption(image.alt);
    return {
      src: image.url,
      alt: caption,
      title: caption,
      width: 2048,
      height: 1536,
      cols: spans[index] || 'col-span-12', // Fallback
    };
  });
});
</script>

